// ariaHand.js — WHEN SHE DOES IT, YOU SEE HER DO IT. 2026-09-05.
//
// Braeden: "if aria ever does anything for the user, like takes control, she
// needs to show it always, like with a cursor clicking, or even typing - a
// fake keyboard needs to come up so the user sees aria is the one doing it."
//
// Until now her moves on her own UI were state changes: the drawer opened,
// the box opened, a title appeared letter by letter, a project was made. All
// of it looked like the app doing things by itself. Nothing on screen said
// SHE did it. This is the thing that says so:
//
//   * HER CURSOR - a second pointer, blue, tagged "Aria", that glides to the
//     control and presses it before the state changes. It is not the
//     person's mouse and never moves theirs.
//   * HER KEYBOARD - a small on-screen keyboard that comes up when she types
//     into a field, each key lighting as the letter lands.
//
// Rules, the same ones every cue in this app follows:
//   1. Never show a click on something that is not on screen. If the target
//      cannot be found, the action still happens - it just is not mimed at
//      a place where nothing is. (The caller decides; `click` resolves false.)
//   2. Pointer-events: none on every layer. She is showing, not blocking.
//   3. Plain DOM, no React state - a cursor gliding at 60fps must not rerender
//      a 14,000-line component on every frame.
//
// API (all safe to call from anywhere, never throws out):
//   click(targets, opts)        -> Promise<boolean>   glide + press, then resolve
//   startTyping(targets)        -> boolean            hand to the field, keyboard up
//   key(ch)                     ->                    light one key
//   stopTyping()                ->                    keyboard down after a beat
//   find(targets)               -> Element|null       first VISIBLE match
//   hide()                      ->                    everything away
import './ariaHand.css';

let layer = null, cursor = null, tag = null, ripple = null, board = null, keys = null, hideTimer = null;
let pos = { x: -100, y: -100 };
let shown = false;

const ROWS = [
  ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0'],
  ['q', 'w', 'e', 'r', 't', 'y', 'u', 'i', 'o', 'p'],
  ['a', 's', 'd', 'f', 'g', 'h', 'j', 'k', 'l', "'"],
  ['⇧', 'z', 'x', 'c', 'v', 'b', 'n', 'm', ',', '.', '⌫'],
  ['space', '↵'],
];

function ensure() {
  if (layer || typeof document === 'undefined') return layer;
  try {
    // one layer, ever: a second module instance (hot reload) must not leave
    // a stale cursor and keyboard behind the live one (measured 9/5)
    for (const old of document.querySelectorAll('.aria-hand')) { try { old.remove(); } catch (_) {} }
    layer = document.createElement('div');
    layer.className = 'aria-hand';
    layer.setAttribute('aria-hidden', 'true');
    cursor = document.createElement('div');
    cursor.className = 'aria-hand__cursor';
    cursor.innerHTML = '<svg viewBox="0 0 24 24" width="26" height="26"><path d="M5 3l14 8.5-6.2 1.3L10 19z" fill="#0a84ff" stroke="#fff" stroke-width="1.6" stroke-linejoin="round"/></svg>';
    tag = document.createElement('div');
    tag.className = 'aria-hand__tag';
    tag.textContent = 'Aria';
    cursor.appendChild(tag);
    ripple = document.createElement('div');
    ripple.className = 'aria-hand__ripple';
    board = document.createElement('div');
    board.className = 'aria-hand__board';
    const cap = document.createElement('div');
    cap.className = 'aria-hand__cap';
    cap.textContent = 'Aria is typing';
    board.appendChild(cap);
    keys = new Map();
    for (const row of ROWS) {
      const r = document.createElement('div');
      r.className = 'aria-hand__row';
      for (const k of row) {
        const el = document.createElement('div');
        el.className = 'aria-hand__key' + (k === 'space' ? ' aria-hand__key--space' : (k.length > 1 ? ' aria-hand__key--wide' : ''));
        el.textContent = k === 'space' ? '' : k;
        r.appendChild(el);
        keys.set(k, el);
      }
      board.appendChild(r);
    }
    layer.appendChild(ripple);
    layer.appendChild(cursor);
    layer.appendChild(board);
    document.body.appendChild(layer);
  } catch (_) { layer = null; }
  return layer;
}

function visible(el) {
  if (!el) return false;
  const r = el.getBoundingClientRect();
  const vw = window.innerWidth, vh = window.innerHeight;
  return r.width > 0 && r.height > 0 && r.right > 4 && r.bottom > 4 && r.left < vw - 4 && r.top < vh - 4;
}

function findByText(term) {
  const t = String(term).toLowerCase();
  for (const el of document.querySelectorAll('button,[role="button"],a[href],input,textarea')) {
    if (!visible(el)) continue;
    const label = ((el.getAttribute('aria-label') || '') + ' ' + (el.textContent || '') + ' ' + (el.placeholder || '')).toLowerCase();
    if (label.includes(t)) return el;
  }
  return null;
}

/** First VISIBLE match of a selector list (a string is one selector; `text:Label` matches by what the control says). */
export function find(targets) {
  try {
    if (!targets) return null;
    if (targets instanceof Element) return visible(targets) ? targets : null;
    const list = Array.isArray(targets) ? targets : [targets];
    for (const s of list) {
      if (!s) continue;
      if (typeof s !== 'string') { if (visible(s)) return s; continue; }
      if (s.startsWith('text:')) { const el = findByText(s.slice(5)); if (el) return el; continue; }
      let els = [];
      try { els = Array.from(document.querySelectorAll(s)); } catch (_) { els = []; }
      for (const el of els) if (visible(el)) return el;
    }
  } catch (_) { /* never throws out */ }
  return null;
}

function place(x, y, ms) {
  cursor.style.transition = ms ? `transform ${ms}ms cubic-bezier(.22,.8,.3,1)` : 'none';
  cursor.style.transform = `translate(${Math.round(x)}px, ${Math.round(y)}px)`;
  pos = { x, y };
}

function show() {
  if (!ensure()) return false;
  if (hideTimer) { clearTimeout(hideTimer); hideTimer = null; }
  if (!shown) {
    // she comes in from the bottom middle of the screen, so the first glide
    // is visibly a journey and not a pointer that was already there
    place(window.innerWidth / 2, window.innerHeight - 80, 0);
    // eslint-disable-next-line no-unused-expressions
    cursor.offsetHeight;
    shown = true;
  }
  layer.classList.add('is-on');
  cursor.classList.add('is-on');
  return true;
}

function lingerThenHide(ms = 1400) {
  if (hideTimer) clearTimeout(hideTimer);
  hideTimer = setTimeout(() => { hideTimer = null; hide(); }, ms);
}

/** Everything away. The next `click` starts from the bottom of the screen again. */
export function hide() {
  try {
    if (!layer) return;
    cursor.classList.remove('is-on');
    board.classList.remove('is-on');
    layer.classList.remove('is-on');
    shown = false;
  } catch (_) {}
}

function glideTo(el, ms) {
  const r = el.getBoundingClientRect();
  const x = r.left + Math.min(r.width * 0.55, r.width - 6);
  const y = r.top + Math.min(r.height * 0.55, r.height - 4);
  const dist = Math.hypot(x - pos.x, y - pos.y);
  const dur = Number.isFinite(ms) ? ms : Math.max(420, Math.min(950, dist * 0.9));
  place(x, y, dur);
  return { x, y, dur };
}

/**
 * Glide to the control and press it. Resolves true when the press has been
 * shown (the caller then makes the change), false when nothing on screen
 * matched - in which case the caller should still make the change, just
 * without a mime.
 */
export function click(targets, opts = {}) {
  return new Promise((resolve) => {
    try {
      const el = find(targets);
      if (!el || !show()) { resolve(false); return; }
      const { x, y, dur } = glideTo(el, opts.ms);
      setTimeout(() => {
        try {
          cursor.classList.add('is-down');
          ripple.style.transform = `translate(${Math.round(x)}px, ${Math.round(y)}px)`;
          ripple.classList.remove('is-go');
          // eslint-disable-next-line no-unused-expressions
          ripple.offsetHeight;
          ripple.classList.add('is-go');
        } catch (_) {}
        setTimeout(() => {
          try { cursor.classList.remove('is-down'); } catch (_) {}
          if (!opts.hold) lingerThenHide(opts.linger);
          resolve(true);
        }, 190);
      }, dur + 60);
    } catch (_) { resolve(false); }
  });
}

/** Hand to the field and the keyboard up. Returns false when the field is not on screen (no keyboard then - she is not typing into nothing). */
export function startTyping(targets) {
  try {
    const el = find(targets);
    if (!el || !show()) return false;
    glideTo(el);
    // The keyboard sits just above the field she is typing into (or just
    // below it when the field is near the top), never on top of it - on a
    // short window a fixed bottom offset landed it across the Ask bar (9/5).
    try {
      const r = el.getBoundingClientRect();
      const vh = window.innerHeight;
      const bh = board.offsetHeight || 190;
      board.style.top = ''; board.style.bottom = '';
      if (r.top - 14 - bh > 8) board.style.bottom = `${Math.round(vh - r.top + 14)}px`;
      else board.style.top = `${Math.round(Math.min(r.bottom + 14, vh - bh - 8))}px`;
    } catch (_) {}
    board.classList.add('is-on');
    return true;
  } catch (_) { return false; }
}

/** Light the key for one character. Unknown characters light nothing. */
export function key(ch) {
  try {
    if (!board || !board.classList.contains('is-on')) return;
    const c = String(ch || '');
    let k = c === ' ' ? 'space' : (c === '\n' ? '↵' : c.toLowerCase());
    let el = keys.get(k);
    if (!el && /[A-Z]/.test(c)) { const s = keys.get('⇧'); if (s) flash(s); }
    if (!el) return;
    flash(el);
  } catch (_) {}
}

function flash(el) {
  el.classList.remove('is-down');
  // eslint-disable-next-line no-unused-expressions
  el.offsetHeight;
  el.classList.add('is-down');
  setTimeout(() => el.classList.remove('is-down'), 150);
}

/** Keyboard down after a beat; the hand lingers a moment, then goes too. */
export function stopTyping(opts = {}) {
  try {
    if (!board) return;
    setTimeout(() => { try { board.classList.remove('is-on'); } catch (_) {} }, opts.after == null ? 500 : opts.after);
    if (!opts.hold) lingerThenHide((opts.after == null ? 500 : opts.after) + 1200);
  } catch (_) {}
}

export default { click, startTyping, key, stopTyping, find, hide };
